import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, User, Lock, Eye, EyeOff, ArrowRight, ShieldCheck, Users, HardHat, BadgeCheck, TrendingUp, UserPlus, X, Mail, CheckCircle2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import InstallAppButton from "../components/InstallAppButton";
import AppQrButton from "../components/AppQrButton";

const HERO_IMG = "https://images.unsplash.com/photo-1591635865172-d500aa5d8a9b?fm=jpg&q=60&w=2000&auto=format&fit=crop";
const NCC_LOGO = "/ncc-logo.png";

const FEATURES = [
  { Icon: Users, label1: "Contractor", label2: "Network" },
  { Icon: HardHat, label1: "Manpower", label2: "Management" },
  { Icon: BadgeCheck, label1: "Verified", label2: "Workforce" },
  { Icon: TrendingUp, label1: "Real-time", label2: "Insights" },
];

export default function AdminLoginPage() {
  const { user, login, loading } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("contractor"); // default per product: Contractor
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false); // default unchecked
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);

  // Warm the backend pod immediately when the login page mounts. This runs
  // 5-30 seconds before the user actually clicks LOGIN, giving the platform
  // enough time to spin up a cold pod so the actual POST /auth/login lands
  // on a warm process. Fire-and-forget — no error handling because failure
  // here doesn't affect the user experience.
  useEffect(() => {
    try {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/health`, {
        method: "GET",
        cache: "no-store",
        // keepalive lets the ping survive brief network flakiness during
        // the initial page load without holding up any UI thread.
        keepalive: true,
      }).catch(() => { /* silent */ });
    } catch { /* silent */ }
  }, []);

  if (loading) return null;
  if (user) return <Navigate to="/admin" replace />;

  async function submit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (mode === "admin") {
        await login(identifier.trim(), password);
        nav("/admin");
      } else {
        // Contractor / Workman: mobile-only login — auto-detect user type.
        // 1) Try contractor (covers Contractor + Payroll Employee records)
        // 2) Fall back to workman roster lookup
        const mobile = identifier.trim();
        try {
          const r = await api.post("/contractor-auth/login", { mobile });
          localStorage.setItem("contractor_token", r.data.access_token);
          localStorage.setItem("contractor_id", r.data.contractor_id);
          nav("/portal");
        } catch (cErr) {
          const status = cErr?.response?.status;
          if (status === 401) {
            const w = await api.post("/workman-auth/login", { mobile });
            localStorage.setItem("workman_token", w.data.access_token);
            localStorage.setItem("workman_id", w.data.workman_id);
            if (w.data.previous_login) {
              localStorage.setItem("workman_prev_login", JSON.stringify(w.data.previous_login));
            } else {
              localStorage.removeItem("workman_prev_login");
            }
            nav("/workman");
          } else {
            throw cErr;
          }
        }
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#f5f7fc]">
      {/* Soft light-blue gradient backdrop */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-gradient-to-b from-[#dbe5fb] via-[#e8eef9] to-transparent" />

      <div className="relative mx-auto max-w-md px-5 pt-12 pb-12">
        {/* Brand block */}
        <div className="flex flex-col items-center text-center">
          <img src={NCC_LOGO} alt="NCC-SWMS · MANPOWER DATABANK" className="w-56 max-w-full" data-testid="login-ncc-logo" />
          <p className="mt-5 max-w-sm text-sm text-slate-700">
            Connecting Contractors.<br />
            Managing Manpower. Building Better.
          </p>
        </div>

        {/* Hero image card with overlapping feature row */}
        <div className="relative mt-8">
          <div
            className="h-52 rounded-2xl bg-cover bg-center shadow-md"
            style={{ backgroundImage: `url('${HERO_IMG}')` }}
            aria-hidden="true"
          />
          {/* Feature row — overlaps the bottom of the hero image */}
          <div className="relative -mt-12 mx-2 rounded-2xl border border-slate-200 bg-white px-3 py-4 shadow-lg" data-testid="login-feature-row">
            <div className="grid grid-cols-4">
              {FEATURES.map((f, i) => (
                <div key={f.label1} className={`flex flex-col items-center px-2 text-center ${i < FEATURES.length - 1 ? "border-r border-slate-200" : ""}`}>
                  <div className="relative">
                    <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-[#fbbf24]" />
                    <f.Icon className="h-7 w-7 text-[#1e3a8a]" strokeWidth={2.2} />
                  </div>
                  <div className="mt-2 text-[10px] font-bold leading-tight text-slate-800">
                    {f.label1}<br />{f.label2}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Login card */}
        <form
          onSubmit={submit}
          className="relative mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg"
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-4">
            {/* Square avatar with thin gold border + soft blue inner circle */}
            <div className="grid h-14 w-14 shrink-0 place-items-center rounded-md border-2 border-[#fbbf24]/70">
              <div className="grid h-11 w-11 place-items-center rounded-full bg-[#eef2ff]">
                <User className="h-6 w-6 text-[#1e3a8a]" strokeWidth={2.2} />
              </div>
            </div>
            <div>
              <h2 className="font-heading text-2xl font-extrabold tracking-tight text-[#1e3a8a]">Welcome to NCC-SWMS</h2>
              <p className="text-sm text-slate-500">New here? Register below, or sign in to your account.</p>
            </div>
          </div>

          {/* ▸ REGISTRATION (FIRST) — Side-by-side Contractor / Payroll */}
          <div className="mt-5">
            <div className="industrial-label mb-2 text-[11px] font-bold tracking-widest text-slate-500">New Registration</div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="login-register-row">
              <button
                type="button"
                onClick={() => nav("/register")}
                data-testid="login-register-contractor-btn"
                className="group flex w-full items-center justify-center gap-3 rounded-lg border-2 border-[#1e3a8a]/30 bg-white px-4 py-3 text-sm text-slate-700 transition-all hover:-translate-y-0.5 hover:border-[#1e3a8a] hover:bg-[#eef2ff] hover:shadow-md"
              >
                <HardHat className="h-5 w-5 text-[#fbbf24]" />
                <span className="font-bold text-[#1e3a8a]">Register as Contractor</span>
              </button>
              <button
                type="button"
                onClick={() => nav("/register/payroll")}
                data-testid="login-register-payroll-btn"
                className="group flex w-full items-center justify-center gap-3 rounded-lg border-2 border-[#1e3a8a]/30 bg-white px-4 py-3 text-sm text-slate-700 transition-all hover:-translate-y-0.5 hover:border-[#1e3a8a] hover:bg-[#eef2ff] hover:shadow-md"
              >
                <BadgeCheck className="h-5 w-5 text-[#fbbf24]" />
                <span className="font-bold text-[#1e3a8a]">Register as New Payroll Manpower</span>
              </button>
            </div>
          </div>

          {/* OR divider */}
          <div className="relative mt-6 flex items-center" aria-hidden="true">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="px-3 text-xs font-semibold tracking-widest text-slate-400">OR · LOGIN</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          {/* Mode toggle */}
          <div className="mt-4 grid grid-cols-2 rounded-lg bg-[#eef2ff] p-1" data-testid="login-mode-tabs">
            <button
              type="button"
              onClick={() => { setMode("admin"); setError(""); }}
              data-testid="login-mode-admin"
              className={`rounded-md py-2 text-xs font-bold tracking-wider transition-colors ${mode === "admin" ? "bg-white text-[#1e3a8a] shadow-sm" : "text-slate-600 hover:text-[#1e3a8a]"}`}
            >
              LOGIN TO TEAM-SWMS
            </button>
            <button
              type="button"
              onClick={() => { setMode("contractor"); setError(""); }}
              data-testid="login-mode-contractor"
              className={`rounded-md py-2 text-xs font-bold tracking-wider transition-colors ${mode === "contractor" ? "bg-white text-[#1e3a8a] shadow-sm" : "text-slate-600 hover:text-[#1e3a8a]"}`}
            >
              CONTRACTOR / WORKMAN
            </button>
          </div>

          {/* Identifier */}
          <label className="mt-4 block">
            <div className="relative">
              <User className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                required
                autoComplete="username"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder={mode === "admin" ? "Email or Mobile Number" : "Registered Mobile Number"}
                data-testid="admin-email"
                className="w-full rounded-lg border border-slate-300 bg-white py-3 pl-11 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#1e3a8a] focus:outline-none focus:ring-2 focus:ring-[#1e3a8a]/15"
              />
            </div>
          </label>

          {/* Password — admins only (contractors log in with mobile only) */}
          {mode === "admin" && (
            <label className="mt-3 block">
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                <input
                  type={showPw ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  data-testid="admin-password"
                  className="w-full rounded-lg border border-slate-300 bg-white py-3 pl-11 pr-11 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#1e3a8a] focus:outline-none focus:ring-2 focus:ring-[#1e3a8a]/15"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  data-testid="admin-password-toggle"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-slate-500 hover:bg-slate-100"
                >
                  {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </label>
          )}

          {/* Remember + Forgot */}
          <div className="mt-3 flex items-center justify-between text-sm">
            <label className="flex cursor-pointer items-center gap-2 text-slate-700">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                data-testid="login-remember"
                className="h-4 w-4 rounded border-slate-300 text-[#1e3a8a] focus:ring-[#1e3a8a]/30"
              />
              Remember Me
            </label>
            {mode === "admin" ? (
              <button
                type="button"
                onClick={() => setForgotOpen(true)}
                data-testid="login-forgot"
                className="font-semibold text-[#1e3a8a] hover:underline"
              >
                Forgot Password?
              </button>
            ) : (
              <span className="text-xs text-slate-500" data-testid="contractor-forgot-hint">
                Forgot password? Ask the admin to reset.
              </span>
            )}
          </div>

          {error && (
            <div className="mt-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs font-medium text-red-700" data-testid="login-error">
              {error}
            </div>
          )}

          {/* Submit — navy fading to gold on the right with arrow */}
          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="relative mt-5 flex h-12 w-full items-center overflow-hidden rounded-lg bg-gradient-to-r from-[#1e3a8a] via-[#1e3a8a] to-[#fbbf24] text-base font-bold tracking-wider text-white shadow-md transition-transform hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-70 disabled:hover:translate-y-0"
          >
            <span className="flex flex-1 items-center justify-center gap-2">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? "SIGNING IN" : "LOGIN"}
            </span>
            <span className="grid h-full w-14 shrink-0 place-items-center">
              <ArrowRight className="h-5 w-5 text-white" />
            </span>
          </button>

          {/* Security note */}
          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="h-4 w-4 text-[#1e3a8a]" />
            Your data is secure with us.
          </div>
        </form>

        {/* Install on device */}
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2" data-testid="login-install-row">
          <InstallAppButton variant="outline" size="md" label="Install App on this device" />
          <AppQrButton variant="outline" size="md" label="Scan to Install" />
        </div>
      </div>

      {forgotOpen && <ForgotPasswordModal onClose={() => setForgotOpen(false)} initial={identifier} />}
      {registerOpen && <RegisterChooserModal onClose={() => setRegisterOpen(false)} />}
    </div>
  );
}

function RegisterChooserModal({ onClose }) {
  const nav = useNavigate();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" data-testid="register-chooser-modal">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-heading text-lg font-extrabold text-[#1e3a8a]">Register as</h3>
            <p className="text-xs text-slate-500">Choose the type of registration that fits you.</p>
          </div>
          <button onClick={onClose} aria-label="close" data-testid="register-chooser-close" className="rounded p-1 text-slate-500 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-5 grid gap-3">
          <button
            type="button"
            onClick={() => nav("/register")}
            data-testid="register-as-contractor"
            className="group flex items-center gap-4 rounded-xl border-2 border-slate-200 bg-white p-4 text-left transition-all hover:-translate-y-0.5 hover:border-[#1e3a8a] hover:bg-[#eef2ff] hover:shadow-md"
          >
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-lg bg-[#eef2ff] group-hover:bg-white">
              <HardHat className="h-6 w-6 text-[#1e3a8a]" />
            </div>
            <div className="flex-1">
              <div className="font-heading text-base font-extrabold text-[#1e3a8a]">Register as Contractor</div>
              <div className="mt-0.5 text-xs text-slate-600">External labour contractor managing their own crew</div>
            </div>
            <ArrowRight className="h-5 w-5 text-[#fbbf24]" />
          </button>

          <button
            type="button"
            onClick={() => nav("/register/payroll")}
            data-testid="register-as-payroll"
            className="group flex items-center gap-4 rounded-xl border-2 border-slate-200 bg-white p-4 text-left transition-all hover:-translate-y-0.5 hover:border-[#1e3a8a] hover:bg-[#eef2ff] hover:shadow-md"
          >
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-lg bg-[#eef2ff] group-hover:bg-white">
              <BadgeCheck className="h-6 w-6 text-[#1e3a8a]" />
            </div>
            <div className="flex-1">
              <div className="font-heading text-base font-extrabold text-[#1e3a8a]">Register as New Payroll Manpower</div>
            </div>
            <ArrowRight className="h-5 w-5 text-[#fbbf24]" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ForgotPasswordModal({ onClose, initial }) {
  const [ident, setIdent] = useState(initial || "");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    if (!ident.trim()) return;
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { identifier: ident.trim() });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not send reset email");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" data-testid="forgot-modal">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#eef2ff]">
              <Mail className="h-5 w-5 text-[#1e3a8a]" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-extrabold text-[#1e3a8a]">Forgot password</h3>
              <p className="text-xs text-slate-500">We&apos;ll email you a secure reset link.</p>
            </div>
          </div>
          <button onClick={onClose} aria-label="close" data-testid="forgot-close" className="rounded p-1 text-slate-500 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        {done ? (
          <div className="mt-5 rounded-md border border-emerald-300 bg-emerald-50 p-4" data-testid="forgot-success">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
              <div className="text-sm text-emerald-900">
                <div className="font-bold">Check your inbox</div>
                <p className="mt-1">If an account exists for that email or mobile, we&apos;ve sent a password reset link. The link expires in 30 minutes.</p>
              </div>
            </div>
            <button onClick={onClose} className="mt-4 w-full rounded-lg bg-[#1e3a8a] py-2.5 text-sm font-bold tracking-wider text-white hover:-translate-y-0.5 transition-transform">DONE</button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5" data-testid="forgot-form">
            <label className="block">
              <span className="text-xs font-semibold tracking-wider text-slate-600">EMAIL OR MOBILE</span>
              <input
                type="text"
                required
                value={ident}
                onChange={(e) => setIdent(e.target.value)}
                placeholder="you@example.com or +91 98765 43210"
                autoFocus
                data-testid="forgot-identifier"
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3 text-sm focus:border-[#1e3a8a] focus:outline-none focus:ring-2 focus:ring-[#1e3a8a]/15"
              />
            </label>
            {error && <div className="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="forgot-error">{error}</div>}
            <button
              type="submit"
              disabled={submitting || !ident.trim()}
              data-testid="forgot-submit"
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-[#1e3a8a] py-2.5 text-sm font-bold tracking-wider text-white shadow-md transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? "SENDING…" : "SEND RESET LINK"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
